import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

type ReminderSetting = { hasConfig: boolean; enabled: boolean };

// Settings → Notifications. The approval reminder email links here ("Don't
// want these emails? Turn them off"), so this switch must keep working.
const NotificationSettings = () => {
  const { businessId } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["approval-notify-enabled", businessId] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    enabled: !!businessId,
    queryFn: async (): Promise<ReminderSetting> => {
      const { data: config, error } = await supabase
        .from("ai_agent_configs")
        .select("approval_notify_enabled")
        .eq("business_id", businessId!)
        .maybeSingle();
      if (error) throw error;
      // No agent config yet means no AI actions to approve, and the reminder
      // job only reads businesses that have one.
      return config
        ? { hasConfig: true, enabled: config.approval_notify_enabled }
        : { hasConfig: false, enabled: false };
    },
  });

  const save = useMutation({
    mutationFn: async (next: boolean) => {
      const { data: rows, error } = await supabase
        .from("ai_agent_configs")
        .update({ approval_notify_enabled: next })
        .eq("business_id", businessId!)
        .select("approval_notify_enabled");
      if (error) throw error;
      if (!rows?.length) throw new Error("Couldn't save this setting. Please try again.");
      return next;
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ReminderSetting>(queryKey);
      queryClient.setQueryData<ReminderSetting>(queryKey, { hasConfig: true, enabled: next });
      return { previous };
    },
    onError: (error, _next, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      toast.error(error instanceof Error ? error.message : "Couldn't save this setting.");
    },
    onSuccess: (next) => {
      toast.success(next ? "Approval reminders are on" : "Approval reminders are off");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const hasConfig = data?.hasConfig ?? false;

  return (
    <main className="space-y-8">
      <header className="min-w-0 space-y-1">
        <h1 className="text-xl font-heading font-semibold text-foreground">Notifications</h1>
        <p className="text-sm text-muted-foreground">Choose what Dibs reminds you about.</p>
      </header>

      <section aria-labelledby="approval-notifications-heading">
        <div className="mb-2 flex items-center gap-4">
          <h2 id="approval-notifications-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
            Approvals
          </h2>
          <div className="h-px flex-1 bg-border" aria-hidden="true" />
        </div>
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="min-w-0 space-y-1">
            <Label htmlFor="approval-reminders" className="text-sm font-medium text-foreground">
              Approval reminders
            </Label>
            <p id="approval-reminders-description" className="text-sm text-muted-foreground">
              {hasConfig || isLoading
                ? "Remind me when actions are waiting for my approval. You'll hear once when something new arrives, then at most every 6 hours while it's still waiting."
                : "Available once your AI assistant is set up."}
            </p>
          </div>
          <Switch
            id="approval-reminders"
            aria-describedby="approval-reminders-description"
            checked={data?.enabled ?? false}
            disabled={isLoading || !hasConfig || save.isPending}
            onCheckedChange={(next) => save.mutate(next)}
          />
        </div>
      </section>
    </main>
  );
};

export default NotificationSettings;
